import { type QueuedChatMessage } from '../../store/chatInstancesStore';
import {
  getUserMessageDisplayText,
  IconButton,
  Text,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  ArrowUp02Icon,
  ArrowDown01Icon,
  CircleIcon,
  DragDropVerticalIcon,
  Edit03Icon,
  Delete02Icon,
} from '@terra/ui-icons';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

type QueuedMessagesPanelProps = {
  queuedMessages: QueuedChatMessage[];
  onSendNow: ({ queueItemId }: { queueItemId: string }) => void;
  onRemove: ({ queueItemId }: { queueItemId: string }) => void;
  onEdit: ({ queueItemId }: { queueItemId: string }) => void;
  onReorder: ({
    fromIndex,
    toIndex,
  }: {
    fromIndex: number;
    toIndex: number;
  }) => void;
};

export const getQueuedMessageText = ({
  queuedMessage,
}: {
  queuedMessage: QueuedChatMessage;
}) => getUserMessageDisplayText({ message: queuedMessage.message });
type QueueActionIconProps = {
  ariaLabel: string;
  children: ReactNode;
  disabled?: boolean;
  onClick: () => void;
};
const QueueActionIcon = ({
  ariaLabel,
  children,
  disabled,
  onClick,
}: QueueActionIconProps) => (
  <IconButton
    variant="ghost"
    aria-label={ariaLabel}
    disabled={disabled}
    color={disabled ? 'fg.subtle' : 'fg.muted'}
    bg="transparent"
    size="xs"
    p={1}
    _hover={disabled ? undefined : { color: 'fg.default', bg: 'transparent' }}
    _disabled={{
      color: 'fg.subtle',
      cursor: 'not-allowed',
    }}
    onClick={onClick}
  >
    {children}
  </IconButton>
);

type SortableQueuedMessageProps = {
  queueMessage: QueuedChatMessage;
  isQueueEditing: boolean;
  editLabel: string;
  sendNowLabel: string;
  removeLabel: string;
  dragLabel: string;
  onSendNow: ({ queueItemId }: { queueItemId: string }) => void;
  onRemove: ({ queueItemId }: { queueItemId: string }) => void;
  onEdit: ({ queueItemId }: { queueItemId: string }) => void;
};
const SortableQueuedMessage = ({
  queueMessage,
  isQueueEditing,
  editLabel,
  sendNowLabel,
  removeLabel,
  dragLabel,
  onSendNow,
  onRemove,
  onEdit,
}: SortableQueuedMessageProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: queueMessage.id });
  const queueMessageText = getQueuedMessageText({
    queuedMessage: queueMessage,
  });
  const isEditing = queueMessage.state === 'editing';
  const style = {
    transform:
      transform != null
        ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
        : undefined,
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      opacity={isDragging ? 0.5 : 1}
      px={2}
      py={0.5}
      bg="transparent"
      width="100%"
      minW={0}
      _hover={{ bg: 'bg.muted' }}
    >
      <VStack alignItems="stretch" gap={1} width="100%" minW={0}>
        <Box
          display="grid"
          gridTemplateColumns="auto auto minmax(0, 1fr) auto"
          gap={2}
          alignItems="center"
          width="100%"
          minW={0}
          minH="22px"
        >
          <IconButton
            variant="ghost"
            aria-label={dragLabel}
            color="fg.muted"
            bg="transparent"
            size="xs"
            p={1}
            cursor="grab"
            _hover={{ color: 'fg.default', bg: 'transparent' }}
            _active={{ cursor: 'grabbing' }}
            {...attributes}
            {...listeners}
          >
            <DragDropVerticalIcon size={13} />
          </IconButton>
          <Box color="fg.muted" display="flex" flexShrink={0}>
            <CircleIcon size={12} />
          </Box>
          <Text
            as="span"
            display="block"
            width="100%"
            minW={0}
            fontSize="xs"
            color="fg.default"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            title={queueMessageText}
          >
            {queueMessageText}
          </Text>
          <HStack gap={0.5} flexShrink={0}>
            <QueueActionIcon
              ariaLabel={editLabel}
              onClick={() => onEdit({ queueItemId: queueMessage.id })}
              disabled={isQueueEditing}
            >
              <Edit03Icon size={13} />
            </QueueActionIcon>
            <QueueActionIcon
              ariaLabel={sendNowLabel}
              onClick={() => onSendNow({ queueItemId: queueMessage.id })}
              disabled={isQueueEditing || isEditing}
            >
              <ArrowUp02Icon size={13} />
            </QueueActionIcon>
            <QueueActionIcon
              ariaLabel={removeLabel}
              onClick={() => onRemove({ queueItemId: queueMessage.id })}
            >
              <Delete02Icon size={13} />
            </QueueActionIcon>
          </HStack>
        </Box>
      </VStack>
    </Box>
  );
};

export const QueuedMessagesPanel = ({
  queuedMessages,
  onSendNow,
  onRemove,
  onEdit,
  onReorder,
}: QueuedMessagesPanelProps) => {
  const t = useTranslations('chat.copilot.queuedMessages');
  const [isExpanded, setIsExpanded] = useState(true);
  const queuedMessageIds = useMemo(
    () => queuedMessages.map((queueMessage) => queueMessage.id),
    [queuedMessages],
  );
  const isQueueEditing = queuedMessages.some(
    (queueMessage) => queueMessage.state === 'editing',
  );
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) {
        return;
      }

      const fromIndex = queuedMessageIds.indexOf(String(active.id));
      const toIndex = queuedMessageIds.indexOf(String(over.id));

      if (fromIndex === -1 || toIndex === -1) {
        return;
      }

      onReorder({ fromIndex, toIndex });
    },
    [onReorder, queuedMessageIds],
  );

  if (queuedMessages.length === 0) {
    return null;
  }

  return (
    <VStack
      alignItems="stretch"
      gap={0.5}
      bg="bg.default"
      borderWidth="1px"
      borderBottomWidth="0"
      borderColor="border.subtle"
      borderTopRadius="md"
      py={1}
      maxH="240px"
      width="100%"
      maxW="100%"
      minW={0}
      overflow="hidden"
      overflowY="auto"
    >
      <HStack
        gap={1}
        px={2}
        minH="22px"
        cursor="pointer"
        onClick={() => setIsExpanded((currentIsExpanded) => !currentIsExpanded)}
      >
        <Box
          color="fg.muted"
          display="flex"
          transform={isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'}
        >
          <ArrowDown01Icon size={14} />
        </Box>
        <Text fontSize="xs" color="fg.muted" fontWeight="medium">
          {t('count', { count: queuedMessages.length })}
        </Text>
      </HStack>
      {isExpanded && (
        <Box width="100%" minW={0} overflow="hidden">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={queuedMessageIds}
              strategy={verticalListSortingStrategy}
            >
              {queuedMessages.map((queueMessage) => (
                <SortableQueuedMessage
                  key={queueMessage.id}
                  queueMessage={queueMessage}
                  isQueueEditing={isQueueEditing}
                  editLabel={t('editLabel')}
                  sendNowLabel={t('sendNowLabel')}
                  removeLabel={t('removeLabel')}
                  dragLabel={t('dragLabel')}
                  onSendNow={onSendNow}
                  onRemove={onRemove}
                  onEdit={onEdit}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Box>
      )}
    </VStack>
  );
};
